import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import MenuList from 'classes/Menu/Search/MenuList';
import Search from 'classes/Menu/Search/Search';
import MenuAdvancedSearch from 'components/desktop/Menu/AdvancedSearch/AdvancedSearch';
import SearchResultListComponent from 'components/desktop/_general/AddMenuOrRecipe/MenusList/SearchResultList';
import DropdownPageSize from 'components/desktop/_general/DropdownPageSize/DropdownPageSize';
import DropdownSortOrder from 'components/desktop/_general/DropdownSortOrder/DropdownSortOrder';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import { MenuSortOrder } from 'enums/SortOrder/MenuSortOrder';
import clone from 'functions/clone';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	SourceOfAddingType: 'menu' | 'menuPlan' | 'menuCopy';
	SourceOfAddingId: string | null;
	SourceOfAddingRow: number;
	SourceOfAddingCol: number;
	finalizeAdding: () => void;
}

const maxNumberOfButtons = 10;

const MenuSearchComponent = (props: IProps) => {
	const { t } = useTranslation();

	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const reduxCurrency = useSelector((state: RootState) => state.currency);
	const reduxPreferredNutrientIds = useSelector((state: RootState) => state.preferredNutrientIds);

	const [isLoading, setIsLoading] = useState(false);
	const [searchIsLoading, setSearchIsLoading] = useState(false);
	const [renderRanges, setRenderRanges] = useState<boolean>(false);

	const [search, setSearch] = useState<Search>(new Search());
	const [menuList, setMenuList] = useState<MenuList>(new MenuList());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		setSearchIsLoading(true);
		await getMenues();
		await search.initialize();
		setSearch(clone(search));
		setSearchIsLoading(false);
	};

	const handleChangeUrl = () => {
		getMenues();
	};

	const handleChangeAdvancedSearchUrl = () => {
		search.pageIndex.value = 0;
		handleChangeUrl();
	};

	const getMenues = async (): Promise<void> => {
		setIsLoading(true);
		const response = await menuList.callApi(
			search.mapToRequest(reduxCurrency, reduxPreferredNutrientIds)
		);
		response.do((x) => {
			menuList.mapFromApi(x);
			setMenuList(clone(menuList));
			search.mapFromApi(x);
		});
		setIsLoading(false);
	};

	const handleReset = () => {
		search.reset();
		setSearchTerm('');
		handleChangeUrl();
	};

	const handleSearch = (input?: string) => {
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangeSortOrder = (value: string) => {
		search.sortOrder.value = value as MenuSortOrder;
		handleChangeUrl();
	};

	const handleChangePageSize = (value: string): void => {
		search.pageSize.value = Number(value);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const getSuggestedMenus = async (): Promise<ISelectItem[]> => {
		await menuList.search(searchTerm, false);
		setMenuList(clone(menuList));
		return menuList.filtered;
	};

	const advancedSearchContainer = document.getElementById('advancedSearch');
	if (advancedSearchContainer) {
		advancedSearchContainer.addEventListener('shown.bs.collapse', function () {
			setRenderRanges(true);
		});
		advancedSearchContainer.addEventListener('hidden.bs.collapse', function () {
			setRenderRanges(false);
		});
	}

	const renderTitle = (): JSX.Element => {
		return <h3>{t('menuPlan:ADD_MENU')}</h3>;
	};

	return (
		<main className="container" style={{ height: '70vh', overflowY: 'auto' }}>
			<LoadingAnimation isLoading={isLoading} isModal={true} />
			<div className="d-flex justify-content-between">{renderTitle()}</div>
			<br />
			<div className="row">
				<div className="col-lg-7">
					<div className="input-group mb-3">
						<AdvancedSearchInput
							debounceMilliSeconds={350}
							getSuggestedItems={getSuggestedMenus}
							handleSearch={handleSearch}
							searchTerm={searchTerm}
							setSearchTerm={setSearchTerm}
							triggerSearchLetterAmount={1}
						/>
					</div>
				</div>
				<div className="col-lg-2">
					<button
						id="advancedSearchButton"
						className="btn btn-primary"
						type="button"
						onClick={() => setRenderRanges(true)}
						data-bs-toggle="collapse"
						data-bs-target="#advancedSearch"
						aria-expanded="true"
						aria-controls="advancedSearch"
						disabled={searchIsLoading}
					>
						{searchIsLoading ? <Spinner animation="border" size="sm" /> : ''}
						{t('_general:ADVANCED_FILTER')}
					</button>
				</div>
				<div className="col-lg-3 text-end">
					<div className="d-flex flex-row justify-content-end">
						<DropdownPageSize
							className="me-2"
							listFunction={handleChangePageSize}
							currentValue={String(search.getPageSize())}
						/>
						<DropdownSortOrder
							type={'menu'}
							listFunction={handleChangeSortOrder}
							currentValue={search.getSortOrder()}
						/>
					</div>
				</div>
			</div>
			<div className={'collapse'} id="advancedSearch">
				<div className="card card-body mb-3">
					<MenuAdvancedSearch
						disabled={menuList.totalCount === 0}
						advancedSearch={search}
						setAdvancedSearch={setSearch}
						handleChangeUrl={handleChangeAdvancedSearchUrl}
						handleReset={handleReset}
						renderRanges={renderRanges}
					/>
				</div>
			</div>

			<p>{`${menuList.totalCount} ${t('_general:MENUES')}`}</p>
			<div className="mb-3">
				<SearchResultListComponent
					menuList={menuList}
					SourceOfAddingCol={props.SourceOfAddingCol}
					SourceOfAddingId={props.SourceOfAddingId}
					SourceOfAddingRow={props.SourceOfAddingRow}
					SourceOfAddingType={props.SourceOfAddingType}
					finalizeAdding={props.finalizeAdding}
				/>
			</div>
			<Pagination
				countOfAllListItems={menuList.totalCount}
				pageIndex={search.getPageIndex()}
				setPageIndex={handleChangePageIndex}
				listItemAmount={search.getPageSize()}
				maxNumberOfButtons={maxNumberOfButtons}
			/>
		</main>
	);
};

export default MenuSearchComponent;
